import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Head_CateHead from "../components/Head_CateHead.js";
import { Footer } from "../components/Footer.js";

const API_BASE = process.env.REACT_APP_API_URL;

// ── tiny helpers ─────────────────────────────────────────────────────────────

const fmt = (val) =>
  val !== undefined && val !== null && val !== ""
    ? val
    : "—";

const money = (val) =>
  val !== undefined && val !== null
    ? `$${parseFloat(val).toFixed(2)}`
    : "—";

// ── component ────────────────────────────────────────────────────────────────

const ManagerReview = () => {
  const { token } = useParams();

  const [order, setOrder]         = useState(null);
  const [details, setDetails]     = useState([]);
  const [status, setStatus]       = useState("loading"); // loading | ready | already | error
  const [actionStatus, setActionStatus] = useState(null); // null | submitting | approved | rejected | error
  const [errorMsg, setErrorMsg]   = useState("");

  // ── fetch order by token ───────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("No review token provided.");
      return;
    }

    axios
      .get(`${API_BASE}frontend/api/order/manager-review/${token}`)
      .then((res) => {
        const { Order: ord, OrderDetails } = res.data.Data;
        if (ord.Status !== "AWAITING CONFIRMATION") {
          setStatus("already");
          setOrder(ord);
        } else {
          setOrder(ord);
          setDetails(OrderDetails || []);
          setStatus("ready");
        }
      })
      .catch((err) => {
        setStatus("error");
        setErrorMsg(
          err.response?.data?.ErrorMessage || "Invalid or expired review link."
        );
      });
  }, [token]);

  // ── approve / reject ───────────────────────────────────────────────────────
  const handleAction = async (action) => {
    if (actionStatus === "submitting") return;
    setActionStatus("submitting");

    try {
      const res = await axios.post(
        `${API_BASE}frontend/api/order/manager-review/${token}`,
        { action }
      );

      if (res.data.AlreadyProcessed) {
        setActionStatus("already");
        setOrder((prev) => ({ ...prev, Status: res.data.Status }));
      } else {
        setActionStatus(action === "APPROVED" ? "approved" : "rejected");
      }
    } catch (err) {
      setActionStatus("error");
      setErrorMsg(
        err.response?.data?.ErrorMessage || "Something went wrong. Please try again."
      );
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────

  const renderContent = () => {
    // ── loading ──────────────────────────────────────────────────────────────
    if (status === "loading") {
      return (
        <div style={styles.centreBox}>
          <div style={styles.spinner} />
          <p style={styles.mutedText}>Loading order details…</p>
        </div>
      );
    }

    // ── error ────────────────────────────────────────────────────────────────
    if (status === "error") {
      return (
        <div style={styles.centreBox}>
          <div style={{ fontSize: "3rem" }}>❌</div>
          <h2 style={{ color: "#c0392b", marginTop: "12px" }}>Link Invalid</h2>
          <p style={styles.mutedText}>{errorMsg}</p>
        </div>
      );
    }

    // ── already processed ────────────────────────────────────────────────────
    if (status === "already" || actionStatus === "already") {
      const st = order?.Status || "";
      return (
        <div style={styles.centreBox}>
          <div style={{ fontSize: "3rem" }}>ℹ️</div>
          <h2 style={{ color: "#e67e22", marginTop: "12px" }}>Already Processed</h2>
          <p style={styles.mutedText}>
            This order has already been actioned.
            <br />
            Current status: <strong>{st}</strong>
          </p>
        </div>
      );
    }

    // ── action done ──────────────────────────────────────────────────────────
    if (actionStatus === "approved") {
      return (
        <div style={styles.centreBox}>
          <div style={{ fontSize: "3.5rem" }}>✅</div>
          <h2 style={{ color: "#27ae60", marginTop: "12px" }}>Order Approved</h2>
          <p style={styles.mutedText}>
            The order has been approved and your team has been notified.
            <br />
            BeBranded will now begin processing this order.
          </p>
        </div>
      );
    }

    if (actionStatus === "rejected") {
      return (
        <div style={styles.centreBox}>
          <div style={{ fontSize: "3.5rem" }}>🚫</div>
          <h2 style={{ color: "#c0392b", marginTop: "12px" }}>Order Rejected</h2>
          <p style={styles.mutedText}>
            The order has been rejected and all parties have been notified.
          </p>
        </div>
      );
    }

    if (actionStatus === "error") {
      return (
        <div style={styles.centreBox}>
          <div style={{ fontSize: "3rem" }}>⚠️</div>
          <h2 style={{ color: "#c0392b", marginTop: "12px" }}>Error</h2>
          <p style={styles.mutedText}>{errorMsg}</p>
        </div>
      );
    }

    // ── main review view ─────────────────────────────────────────────────────
    return (
      <div style={styles.card}>

        {/* Header */}
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>Order Review</h2>
          <span style={styles.badge}>AWAITING YOUR APPROVAL</span>
        </div>

        {/* Billing summary */}
        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>Customer Details</h3>
          <div style={styles.grid2}>
            <Detail label="Customer Name"  value={fmt(order.BCustomerName)} />
            <Detail label="Email"          value={fmt(order.BEmail)} />
            <Detail label="Mobile"         value={fmt(order.BMobileNumber)} />
            <Detail label="Order Total"    value={money(order.Totalprice)} />
            <Detail label="GST"            value={money(order.GSTAmount)} />
            <Detail label="PO Number"      value={fmt(order.ponumber)} />
          </div>
        </section>

        {/* Billing address */}
        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>Billing Address</h3>
          <p style={styles.addressBlock}>
            {[
              order.BAddress1,
              order.BAddress2,
              order.BCity,
              order.BState,
              order.BZipcode,
              order.BCountry,
            ]
              .filter(Boolean)
              .join(", ")}
          </p>
        </section>

        {/* Order items */}
        <section style={styles.section}>
          <h3 style={styles.sectionTitle}>Order Items</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.theadRow}>
                  <th style={styles.th}>Product</th>
                  <th style={styles.th}>Qty</th>
                  <th style={styles.th}>Unit Price</th>
                  <th style={styles.th}>Line Total</th>
                </tr>
              </thead>
              <tbody>
                {details.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ ...styles.td, textAlign: "center", color: "#888" }}>
                      No items found.
                    </td>
                  </tr>
                ) : (
                  details.map((item, i) => (
                    <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <td style={styles.td}>
                        {item.Product?.ProductImage && (
                          <img
                            src={`${process.env.REACT_APP_API_URL}${item.Product.ProductImage}`}
                            alt={item.ProductName}
                            style={styles.thumbImg}
                          />
                        )}
                        <span>{fmt(item.ProductName)}</span>
                      </td>
                      <td style={{ ...styles.td, textAlign: "center" }}>{fmt(item.QTY)}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>{money(item.ProductPrice)}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>
                        {money((parseFloat(item.ProductPrice) || 0) * (parseInt(item.QTY) || 0))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ ...styles.td, textAlign: "right", fontWeight: 700 }}>
                    GST:
                  </td>
                  <td style={{ ...styles.td, textAlign: "right", fontWeight: 700 }}>
                    {money(order.GSTAmount)}
                  </td>
                </tr>
                <tr style={{ background: "#e8f7fd" }}>
                  <td colSpan={3} style={{ ...styles.td, textAlign: "right", fontWeight: 700 }}>
                    Total:
                  </td>
                  <td style={{ ...styles.td, textAlign: "right", fontWeight: 700, color: "#01AEF0" }}>
                    {money(order.Totalprice)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        {/* Action buttons */}
        <section style={styles.actionRow}>
          <p style={{ ...styles.mutedText, marginBottom: "18px" }}>
            Please review the order above and choose an action. This link is single-use — once
            you approve or reject, it will expire.
          </p>
          <div style={styles.btnRow}>
            <button
              style={{
                ...styles.btn,
                background: actionStatus === "submitting" ? "#aaa" : "#27ae60",
              }}
              disabled={actionStatus === "submitting"}
              onClick={() => handleAction("APPROVED")}
            >
              {actionStatus === "submitting" ? "Processing…" : "✓  Approve Order"}
            </button>
            <button
              style={{
                ...styles.btn,
                background: actionStatus === "submitting" ? "#aaa" : "#e74c3c",
              }}
              disabled={actionStatus === "submitting"}
              onClick={() => handleAction("CANCELED")}
            >
              {actionStatus === "submitting" ? "Processing…" : "✕  Reject Order"}
            </button>
          </div>
        </section>

      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Head_CateHead />
      <main style={styles.main}>{renderContent()}</main>
      <Footer />
    </div>
  );
};

// ── tiny presentational helper ────────────────────────────────────────────────
const Detail = ({ label, value }) => (
  <div style={{ marginBottom: "8px" }}>
    <span style={{ fontSize: "12px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" }}>
      {label}
    </span>
    <p style={{ margin: "2px 0 0", fontSize: "15px", color: "#252525", fontWeight: 500 }}>{value}</p>
  </div>
);

// ── styles ────────────────────────────────────────────────────────────────────
const styles = {
  main: {
    flex: 1,
    padding: "32px 16px",
    background: "#f4f7fa",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  card: {
    width: "100%",
    maxWidth: "860px",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  cardHeader: {
    background: "#01AEF0",
    padding: "20px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "10px",
  },
  cardTitle: {
    color: "#fff",
    margin: 0,
    fontSize: "22px",
    fontWeight: 700,
  },
  badge: {
    background: "#fff",
    color: "#01AEF0",
    fontSize: "12px",
    fontWeight: 700,
    padding: "4px 14px",
    borderRadius: "50px",
    letterSpacing: "0.5px",
  },
  section: {
    padding: "22px 28px",
    borderBottom: "1px solid #f0f0f0",
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "#01AEF0",
    margin: "0 0 14px",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "14px 24px",
  },
  addressBlock: {
    margin: 0,
    fontSize: "15px",
    color: "#444",
    lineHeight: 1.6,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },
  theadRow: {
    background: "#f0f8ff",
  },
  th: {
    padding: "10px 12px",
    textAlign: "left",
    fontWeight: 700,
    color: "#0b3b5c",
    borderBottom: "2px solid #01AEF0",
  },
  td: {
    padding: "10px 12px",
    verticalAlign: "middle",
    color: "#333",
    borderBottom: "1px solid #f0f0f0",
  },
  trEven: { background: "#fff" },
  trOdd:  { background: "#fafcfe" },
  thumbImg: {
    width: "40px",
    height: "40px",
    objectFit: "contain",
    marginRight: "10px",
    verticalAlign: "middle",
    borderRadius: "4px",
  },
  actionRow: {
    padding: "28px 28px 32px",
    textAlign: "center",
  },
  btnRow: {
    display: "flex",
    gap: "16px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  btn: {
    color: "#fff",
    border: "none",
    borderRadius: "50px",
    padding: "14px 40px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
    letterSpacing: "0.3px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    transition: "opacity 0.2s",
  },
  centreBox: {
    textAlign: "center",
    padding: "60px 24px",
    maxWidth: "480px",
    margin: "0 auto",
  },
  mutedText: {
    color: "#666",
    fontSize: "15px",
    lineHeight: 1.6,
    marginTop: "10px",
  },
  spinner: {
    width: "44px",
    height: "44px",
    border: "4px solid #e0e0e0",
    borderTop: "4px solid #01AEF0",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto 16px",
  },
};

export default ManagerReview;
